import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, signal, Signal, SimpleChanges, TemplateRef, ViewChild, WritableSignal, OnChanges, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '@app/_services/modal.service';
import { Entry } from '@app/_models/entry.model';
import { CacheService } from '@app/_services/cache.service';
import { CalendarModule, CalendarEvent, CalendarView } from 'angular-calendar';
// NOTE: Form components are loaded dynamically to break circular dependencies
import { CalendarIntegrationStore, FocusStore, HabitStore, JournalStore, MoneyTrackerStore, TodoStore } from '@app/_stores';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { SettingsService } from '@app/_services/settings.service';
import { EntityEntriesComponent } from '../entity-entries/entity-entries.component';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '@app/_services/analytics.service';
import { log } from '@app/_services/logger';
import { FocusAction, FocusSession, FocusSetup } from '@app/_interfaces/focus-tracker.interface';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';

@Component({
  selector: 'app-entity-calendar',
  imports: [
    CommonModule,
    CalendarModule,
    MeUserAvatarComponent,
    SvgComponent
  ],
  templateUrl: './entity-calendar.component.html',
  styleUrl: './entity-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class EntityCalendarComponent implements OnChanges, OnInit, OnDestroy {
  readonly focusStore = inject(FocusStore);
  readonly moneyTrackerStore = inject(MoneyTrackerStore);
  @ViewChild('customEventTemplate') customEventTemplate!: TemplateRef<any>;
  view: CalendarView = CalendarView.Day;

  @Input() viewDate: Date = new Date();
  @Input() dateString: string = getDateString();
  @Input() events: CalendarEvent[] = [];
  @Input() withoutTimeEvents: Entry[] = [];
  withoutTimeEventsSignal: WritableSignal<Entry[]> = signal([]);

  // Signal to track raw events for processing
  rawEventsSignal: WritableSignal<CalendarEvent[]> = signal([]);

  // Processed events with "view more" logic
  processedEvents: Signal<CalendarEvent[]> = computed(() => {
    return this.processEventsWithViewMore(this.rawEventsSignal());
  });

  // All untimed events in one shared chip row — no special rows per entity type.
  // Matches Flutter's calendar view categorization.
  unscheduledEvents: Signal<Entry[]> = computed(() => {
    return this.withoutTimeEventsSignal().filter(event => event.totalDays <= 1);
  });

  multiDayEndAtEvents: Signal<Entry[]> = computed(() => {
    return this.withoutTimeEventsSignal().filter(event => event.totalDays > 1);
  });

  hasHeaderContent: Signal<boolean> = computed(() => {
    return this.unscheduledEvents().length > 0 ||
      this.multiDayEndAtEvents().length > 0;
  });

  focusEvents: Signal<any[]> = computed(() => {
    const data = this.withoutTimeEventsSignal().filter(event => event.entityType === 'focus');
    const focusMap = data.map((item) => {
      const focusSetup = this.focusStore.setupsMap()[item.setupId];
      if (!focusSetup || !this.dateString) {
        return {
          ...focusSetup,
          sortTitle: focusSetup?.title ?? '',
          bestLevel: 0,
          sessionCount: 0,
        };
      }

      // Get sessions from actions for this setup on this date
      const setupActions = this.focusStore.setupToActions()[item.setupId] || [];
      const allSessions: FocusSession[] = setupActions.flatMap(a => Object.values(a.sessions ?? {}));
      const sessions: FocusSession[] = allSessions.filter(
        session => getDateString(session.startedAt) === this.dateString
      );

      if (sessions.length === 0) {
        return {
          ...focusSetup,
          sortTitle: focusSetup.title,
          bestLevel: 0,
          sessionCount: 0,
        };
      }

      const best = sessions.reduce((a, b) =>
        a.levelReached > b.levelReached ? a : b
      );

      return {
        ...focusSetup,
        sortTitle: focusSetup.title,
        bestLevel: best.levelReached,
        sessionCount: sessions.length,
      };
    });
    return focusMap;
  });

  readonly todoStore = inject(TodoStore);
  readonly habitStore = inject(HabitStore);
  readonly journalStore = inject(JournalStore);
  readonly calendarStore = inject(CalendarIntegrationStore);

  device: WritableSignal<'mobile' | 'tablet' | 'desktop'> = signal('desktop');

  // Minimum tile width (px) used to compute how many columns fit on screen.
  // Bounded between 2 and 8 columns.
  private static readonly MIN_TILE_WIDTH = 90;
  private static readonly MIN_COLUMNS = 4;
  private static readonly MAX_COLUMNS = 8;

  private get maxColumns(): number {
    const width = typeof window !== 'undefined' ? window.innerWidth : 375;
    return Math.min(
      EntityCalendarComponent.MAX_COLUMNS,
      Math.max(
        EntityCalendarComponent.MIN_COLUMNS,
        Math.floor(width / EntityCalendarComponent.MIN_TILE_WIDTH)
      )
    );
  }

  // Maximum number of event tiles that can fit in a row (including "view more" tile)
  get maxEventsPerRow(): number { return this.maxColumns; }

  // Maximum events per time slot for scheduled events
  get maxEventsPerTimeSlot(): number { return this.maxColumns; }

  // Dynamic visible count for focus events
  // If <= maxEventsPerRow: show all
  // If > maxEventsPerRow: show (maxEventsPerRow - 1) + "view more"
  focusVisibleCount: Signal<number> = computed(() => {
    const count = this.focusEvents().length;
    return count <= this.maxEventsPerRow ? count : this.maxEventsPerRow - 1;
  });

  // Dynamic visible count for unscheduled events
  unscheduledVisibleCount: Signal<number> = computed(() => {
    const count = this.unscheduledEvents().length;
    return count <= this.maxEventsPerRow ? count : this.maxEventsPerRow - 1;
  });

  // Computed signals for actual displayed count in each row (used for dynamic width calculation)
  // This includes the "view more" tile if present
  focusEventsDisplayCount: Signal<number> = computed(() => {
    const count = this.focusEvents().length;
    return count > this.maxEventsPerRow ? this.maxEventsPerRow : count;
  });

  unscheduledEventsDisplayCount: Signal<number> = computed(() => {
    const count = this.unscheduledEvents().length;
    return count > this.maxEventsPerRow ? this.maxEventsPerRow : count;
  });

  // Check if current device is mobile
  isMobileDevice(): boolean {
    return this.device() === 'mobile';
  }

  // Calculate dynamic width for event tiles based on count
  // Formula: (100% - total_gaps) / displayCount
  // where total_gaps = (displayCount - 1) * gap_size
  getEventTileWidth(displayCount: number): string {
    if (displayCount <= 1) {
      return '100%';
    }
    // gap is 2px (--spacing-xxxs from the parent container)
    const gapSize = 2;
    const totalGaps = (displayCount - 1) * gapSize;
    return `calc((100% - ${totalGaps}px) / ${displayCount})`;
  }

  // Calculate available width for text in pixels
  private getAvailableTextWidth(displayCount: number, hasUserBadge: boolean): number {
    // Get screen width (use a reasonable estimate or actual viewport width)
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 375;

    // Account for wrapper padding (8px on each side = 16px total)
    const wrapperPadding = 16;

    // Calculate tile width
    const gapSize = 2;
    const totalGaps = (displayCount - 1) * gapSize;
    const tileWidth = (viewportWidth - wrapperPadding - totalGaps) / displayCount;

    // Subtract tile padding (4px on each side = 8px total)
    const tilePadding = 8;

    // Subtract gap between title and icon (8px)
    const gapBetweenElements = 8;

    // Subtract user badge width if present (approximately 20px with margin)
    const userBadgeWidth = hasUserBadge ? 20 : 0;

    // Subtract icon/score width (approximately 20px for score or 12px for checkbox)
    const iconWidth = 20;

    // Calculate available width for text
    const availableWidth = tileWidth - tilePadding - gapBetweenElements - userBadgeWidth - iconWidth;

    return Math.max(0, availableWidth);
  }

  // Truncate text to fit within available width
  // Uses calculated character width with proportional safety buffer
  truncateTitle(title: string, displayCount: number, hasUserBadge: boolean = false): string {
    const availableWidth = this.getAvailableTextWidth(displayCount, hasUserBadge);

    // Use average character width for 12px font
    // Typical range: 4px (i, l) to 10px (w, m), average ~7px
    const avgCharWidth = 7;

    // Calculate max characters that can fit
    const rawMaxChars = Math.floor(availableWidth / avgCharWidth);

    // Use proportional safety buffer instead of fixed -4
    // For small tiles, use smaller buffer; for large tiles, use larger buffer
    // Buffer = 15% of available chars, minimum 1, maximum 2
    const safetyBuffer = Math.min(2, Math.max(1, Math.floor(rawMaxChars * 0.15)));

    let maxChars = Math.max(0, rawMaxChars - safetyBuffer);

    // If title fits, return as-is
    if (title.length <= maxChars || maxChars === 0) {
      return title;
    }

    // Truncate to fit exactly (no partial characters)
    return title.substring(0, maxChars);
  }

  // Convenience methods for each event type
  truncateFocusTitle(title: string, _hasUserBadge: boolean = false): string {
    return title;
  }

  truncateUnscheduledTitle(title: string, _hasUserBadge: boolean = false): string {
    return title;
  }

  // Scheduled event titles: CSS handles overflow via .event-title-text
  // (white-space: nowrap; overflow: hidden; text-overflow: clip)
  // mwl-calendar sizes each event based on actual overlaps, so JS truncation is not needed.
  truncateScheduledTitle(title: string, _hasUserBadge: boolean = false): string {
    return title;
  }

  // Process events with overlap detection and "view more" per start time
  processEventsWithViewMore(events: CalendarEvent[]): CalendarEvent[] {
    if (!events || events.length === 0) return [];

    const maxColumns = this.maxEventsPerTimeSlot;
    const processedEvents: CalendarEvent[] = [];

    // Step 1: Group events by exact start time
    const eventsByTime = new Map<string, CalendarEvent[]>();
    events.forEach(event => {
      const timeKey = this.getTimeKey(event.start);
      if (!eventsByTime.has(timeKey)) {
        eventsByTime.set(timeKey, []);
      }
      eventsByTime.get(timeKey)!.push(event);
    });

    // Pre-compute occupied columns per time slot once using the original events array.
    // Avoids the O(n²) pattern of scanning the growing processedEvents array on each iteration.
    const occupiedColumnsMap = new Map<string, number>();
    eventsByTime.forEach((_, timeKey) => {
      const timeMs = new Date(timeKey).getTime();
      let count = 0;
      events.forEach(event => {
        if (!event.start || !event.end) return;
        if (this.getTimeKey(event.start) === timeKey) return; // same group — skip
        const startMs = new Date(event.start).getTime();
        const endMs = new Date(event.end).getTime();
        if (startMs <= timeMs && timeMs < endMs) count++;
      });
      occupiedColumnsMap.set(timeKey, count);
    });

    // Step 2: Process each time slot separately
    eventsByTime.forEach((timeEvents, timeKey) => {
      // Sort events at this time: longest duration first (AC8)
      // Events are already grouped by start time, so within each group
      // we sort by duration DESC (longest gets leftmost column)
      timeEvents.sort((a, b) => {
        const durationA = this.getEventDurationInMinutes(a);
        const durationB = this.getEventDurationInMinutes(b);
        if (durationB !== durationA) return durationB - durationA;
        return (a.title || '').localeCompare(b.title || '');
      });

      const occupiedColumns = occupiedColumnsMap.get(timeKey) ?? 0;
      const availableSlots = Math.max(0, maxColumns - occupiedColumns);

      if (timeEvents.length <= availableSlots) {
        processedEvents.push(...timeEvents);
      } else if (timeEvents.length === availableSlots + 1) {
        // When only 1 event overflows, show all events directly instead of
        // replacing one with "+N more" — a real event tile is more useful.
        processedEvents.push(...timeEvents);
      } else {
        const visibleCount = Math.max(0, availableSlots - 1);
        const visibleEvents = timeEvents.slice(0, visibleCount);
        const hiddenEvents = timeEvents.slice(visibleCount);
        processedEvents.push(...visibleEvents);
        processedEvents.push(this.createViewMoreEvent(new Date(timeKey), hiddenEvents, timeKey));
      }
    });

    return processedEvents;
  }

  /**
   * Get number of columns occupied at a specific time by existing events
   * IMPORTANT: This counts both visible events AND hidden events inside "View More" events
   * to accurately calculate how many columns are truly occupied at a given time.
   */
  private getOccupiedColumnsAtTime(existingEvents: CalendarEvent[], time: Date): number {
    const timeMs = time.getTime();
    let count = 0;

    existingEvents.forEach(event => {
      // Case 1: Regular event - check if it overlaps with the time point
      if (event.meta?.entity !== 'viewMore') {
        if (!event.start || !event.end) return;

        const startMs = new Date(event.start).getTime();
        const endMs = new Date(event.end).getTime();

        // Check if this time point falls within the event's range
        if (startMs <= timeMs && timeMs < endMs) {
          count++;
        }
      }
      // Case 2: "View More" event - check its hidden events
      // Previously, we skipped "View More" events entirely, but this caused undercounting
      // because hidden events that overlap with the current time weren't included.
      else {
        const hiddenEvents = event.meta?.hiddenEvents || [];

        hiddenEvents.forEach((hiddenEvent: CalendarEvent) => {
          if (!hiddenEvent.start || !hiddenEvent.end) return;

          const startMs = new Date(hiddenEvent.start).getTime();
          const endMs = new Date(hiddenEvent.end).getTime();

          // Check if this hidden event overlaps with the time point
          if (startMs <= timeMs && timeMs < endMs) {
            count++;
          }
        });
      }
    });

    return count;
  }

  // Generate exact time key for grouping (e.g., "2024-01-20T02:30:00.000Z")
  // Events with the same start time will be grouped together
  getTimeKey(date: Date): string {
    return new Date(date).toISOString();
  }

  /**
   * Calculate event duration in minutes
   * Used for sorting events by duration (longest first)
   * Matches Flutter's duration-based sorting logic
   */
  private getEventDurationInMinutes(event: CalendarEvent): number {
    if (!event.start) {
      return 0;
    }

    if (!event.end) {
      return 15; // Default minimum duration for events without end time
    }

    const startTime = new Date(event.start).getTime();
    const endTime = new Date(event.end).getTime();

    // Calculate duration even if end is on next day
    const durationMs = endTime - startTime;
    return Math.max(15, Math.floor(durationMs / 60000)); // Minimum 15 minutes
  }

  /**
   * Check if two events overlap in time
   * Classic interval overlap: a.start < b.end AND a.end > b.start
   * Matches Flutter's isOverlapping function
   */
  private eventsOverlap(a: CalendarEvent, b: CalendarEvent): boolean {
    if (a.id === b.id) return false;
    if (!a.start || !a.end || !b.start || !b.end) return false;

    const aStart = new Date(a.start).getTime();
    const aEnd = new Date(a.end).getTime();
    const bStart = new Date(b.start).getTime();
    const bEnd = new Date(b.end).getTime();

    // Two intervals overlap if: a.start < b.end AND a.end > b.start
    return aStart < bEnd && aEnd > bStart;
  }

  /**
   * Assign column numbers to events based on overlaps
   * Events that overlap must be in different columns
   * Matches Flutter's column assignment algorithm
   */
  private assignColumnsToEvents(events: CalendarEvent[]): void {
    const columns: CalendarEvent[][] = [];

    for (const event of events) {
      let placed = false;

      // Try to place event in existing columns
      for (let i = 0; i < columns.length; i++) {
        // Check if event overlaps with any event already in this column
        const canPlace = !columns[i].some(e => this.eventsOverlap(event, e));

        if (canPlace) {
          // No overlap, can place in this column
          columns[i].push(event);
          event.meta = { ...event.meta, columnNum: i + 1 };
          placed = true;
          break;
        }
      }

      // If couldn't fit in any existing column, create new column
      if (!placed) {
        columns.push([event]);
        event.meta = { ...event.meta, columnNum: columns.length };
      }
    }

  }

  /**
   * Create "View More" event for overflow events (column > 4)
   * Calculates time range based on earliest start and latest end of overflow events
   */
  createViewMoreForOverflow(overflowEvents: CalendarEvent[]): CalendarEvent {
    if (overflowEvents.length === 0) {
      throw new Error('Cannot create view more event with no overflow events');
    }

    // Find earliest start time and latest end time among overflow events
    const startTimes = overflowEvents
      .map(e => e.start)
      .filter((s): s is Date => s !== undefined)
      .map(s => new Date(s).getTime());

    const endTimes = overflowEvents
      .map(e => e.end)
      .filter((e): e is Date => e !== undefined)
      .map(e => new Date(e).getTime());

    const earliestStart = new Date(Math.min(...startTimes));
    const latestEnd = new Date(Math.max(...endTimes));

    // Use 30 minute duration for view more event (smaller visual size)
    const viewMoreStart = earliestStart;
    const viewMoreEnd = new Date(earliestStart.getTime() + 30 * 60 * 1000);

    const timeKey = earliestStart.toISOString();

    return {
      id: `view-more-overflow-${timeKey}`,
      start: viewMoreStart,
      end: viewMoreEnd,
      title: this.ss.t('common_collapsedNumberOfEntities', {
        count: overflowEvents.length.toString()
      }),
      meta: {
        entity: 'viewMore',
        hiddenEvents: overflowEvents,
        timeKey,
        columnNum: this.maxEventsPerTimeSlot // Place in last visible column
      }
    };
  }

  // Create a special "view more" event (legacy method, kept for compatibility)
  createViewMoreEvent(startTime: Date, hiddenEvents: CalendarEvent[], timeKey: string): CalendarEvent {
    const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);

    return {
      id: `view-more-${timeKey}`,
      start: startTime,
      end: endTime,
      title: this.ss.t('common_collapsedNumberOfEntities', {
        count: hiddenEvents.length.toString()
      }),
      meta: {
        entity: 'viewMore',
        hiddenEvents: hiddenEvents,
        timeKey: timeKey
      },
      cssClass: 'view-more-event'
    };
  }

  unSubscribe = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    public cc: CacheService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['withoutTimeEvents']) {
      this.withoutTimeEventsSignal.set(this.withoutTimeEvents);
    }
    if (changes['events']) {
      this.rawEventsSignal.set(this.events);
    }

    // CRITICAL: Mark for check to ensure UI updates when calendar events are deleted
    // in third-party apps (Google/Microsoft Calendar). Without this, the component
    // won't detect sync updates until page reload.
    this.cdr.markForCheck();
  }

  ngOnInit() {
    this.analyticsInit();
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        this.device.set(this.as.commonProperties.device_type as 'mobile' | 'tablet' | 'desktop');
      }
    });
  }

  getProgressBg(percentage: number) {
    return `conic-gradient(${this.ss.theme().color2} ${percentage * 3.6}deg, ${this.ss.theme().color6} 0deg)`;
  }

  viewMoreEntities(type: 'ownerFocus' | 'collaboratedFocus' | 'moneyTracker' | 'other') {
    const entities = this.unscheduledEvents().slice(this.unscheduledVisibleCount(), this.unscheduledEvents().length);
    const entityIds = entities.map(e => e.setupId);
    this.modalService.openLevel2(
      EntityEntriesComponent,
      {
        entityIds: entityIds,
        dateString: this.dateString,
        title: type === 'ownerFocus' ? this.ss.t('common_focusTracker') : type === 'collaboratedFocus' ? this.ss.t('common_focusTracker') : type === 'moneyTracker' ? this.ss.t('common_moneyTracker') : this.ss.t('viewMore_titleTopBar')
      }
    );
  }

  // Handle "view more" click for scheduled events
  async viewMoreScheduledEvents(hiddenEvents: CalendarEvent[], timeKey: string) {
    if (!hiddenEvents || hiddenEvents.length === 0) {
      log.error('viewMoreScheduledEvents', 'No hidden events found');
      return;
    }

    const eventIds = hiddenEvents.map(e => e.id as string);
    const validEventIds = eventIds.filter(id => id && id !== 'undefined');

    if (validEventIds.length === 0) {
      log.error('viewMoreScheduledEvents', 'No valid event IDs found');
      return;
    }

    // Use EntityEntriesComponent with level 2 modal
    this.modalService.openLevel2(
      EntityEntriesComponent,
      {
        entityIds: validEventIds,
        dateString: this.dateString,
        title: this.ss.t('viewMore_titleTopBar'),
        includeScheduled: true  // IMPORTANT: Include scheduled events
      }
    );
  }

  async openScores(id: string) {
    const { FocusScoresComponent } = await import('../../focus-tracker/focus-scores/focus-scores.component');
    this.modalService.openLevel2(
      FocusScoresComponent,
      {
        focusId: id,
        dateString: this.dateString,
      }
    );
  }

  handleScheduledEvent(event: CalendarEvent): void {
    if (event.meta.entity === 'viewMore') return;
    switch (event.meta.entity) {
      case 'todo':
        this.openTodo(event.id as string);
        break;
      case 'journal':
        this.openJournal(event.id as string);
        break;
      case 'habit':
        this.openHabit(event.id as string);
        break;
      case 'calendarIntegration':
        this.openCalendarEvent(event.id as string);
        break;
      case 'focus':
        this.openScores(event.id as string);
        break;
      case 'moneyTracker':
        this.openMoneyTracker(event);
        break;
      default:
        log.warn('EntityCalendarComponent.handleScheduledEvent', `Unknown entity: ${event.meta.entity}`, { entity: event.meta.entity });
        break;
    }
  }

  handleUnScheduledEvent(event: Entry): void {
    switch (event.entityType) {
      case 'journal':
        this.openJournal(event.setupId);
        break;
      case 'todo':
        this.openTodo(event.setupId);
        break;
      case 'habit':
        this.openHabit(event.setupId);
        break;
      case 'calendarIntegration':
        this.openCalendarEvent(event.setupId);
        break;
      case 'moneyTracker':
        this.openMoneyTrackerFromEntry(event);
        break;
      default:
        log.warn('EntityCalendarComponent.handleUnScheduledEvent', `Unknown entityType: ${event.entityType}`, { entityType: event.entityType });
        break;
    }
  }

  async openJournal(setupId: string) {
    const journal = this.journalStore.journalMap().setups[setupId]?.[this.dateString] || null;

    const { JournalFormComponent } = await import('../../journals/journal-form/journal-form.component');
    this.modalService.openLevel1(
      JournalFormComponent,
      {
        mode: journal ? 'edit' : 'new',
        value: journal,
        setupId: setupId,
        dateString: this.dateString
      },
      { disableClose: true, autoFocus: false }
    );
  }

  async openTodo(id: string) {
    const todo = this.todoStore.idToTodo()[id] || null;

    const { TodoFormComponent } = await import('../../todos/todo-form/todo-form.component');
    this.modalService.openLevel1(
      TodoFormComponent,
      {
        mode: 'edit',
        value: todo,
        dateString: this.dateString
      },
      { disableClose: true }
    );
  }

  async openHabit(setupId: string) {
    const habit = this.habitStore.habitMap().setups[setupId]?.[this.dateString] || null;
    const { HabitFormComponent } = await import('../../habits/habit-form/habit-form.component');
    this.modalService.openLevel1(
      HabitFormComponent,
      {
        mode: 'edit',
        value: habit,
        dateString: this.dateString,
        setupId: setupId
      },
      { disableClose: true, autoFocus: false }
    );
  }

  async openCalendarEvent(id: string) {
    const event = this.calendarStore.idToEventSetup()[id] || null;

    const { CalendarEventFormComponent } = await import('../../calendar-integration/calendar-event-form/calendar-event-form.component');

    this.modalService.openLevel1(
      CalendarEventFormComponent,
      {
        mode: 'edit',
        value: event,
        dateString: this.dateString
      },
      { disableClose: true, autoFocus: false }
    );

  }

  async openMoneyTrackerFromEntry(entry: Entry) {
    const setupId = entry.setupId;
    const scheduleId = entry.scheduleId ?? null;
    const actionId = entry.actionId ?? null;
    const occurDate = entry.occurDate ?? this.dateString;

    const transaction = this.moneyTrackerStore.getOrCreateTransaction(
      setupId,
      occurDate,
      scheduleId,
      actionId,
    );
    const isExisting = transaction &&
        this.moneyTrackerStore.idToTransaction()[transaction.id] != null;

    const { MoneyTransactionFormComponent } = await import(
      '../../money-tracker/money-transaction-form/money-transaction-form.component'
    );
    this.modalService.openLevel1(
      MoneyTransactionFormComponent,
      {
        mode: isExisting ? 'edit' : 'new',
        value: isExisting ? transaction : null,
        transactionType: transaction?.transactionType ?? 'expense',
        scheduleId,
        setupId,
        dueDate: occurDate,
      },
      { disableClose: true, autoFocus: false },
    );
  }

  async openMoneyTracker(event: CalendarEvent) {
    const setupId = event.meta?.setupId;
    const scheduleId = event.meta?.scheduleId ?? null;
    const occurDate = event.meta?.occurDate ?? this.dateString;

    const actionId = event.meta?.actionId ?? null;

    // Find existing transaction — actionId for direct lookup, scheduleId for schedule match
    const transaction = this.moneyTrackerStore.getOrCreateTransaction(
      setupId,
      occurDate,
      scheduleId,
      actionId,
    );
    const isExisting = transaction && this.moneyTrackerStore.idToTransaction()[transaction.id] != null;

    const { MoneyTransactionFormComponent } = await import(
      '../../money-tracker/money-transaction-form/money-transaction-form.component'
    );
    this.modalService.openLevel1(
      MoneyTransactionFormComponent,
      {
        mode: isExisting ? 'edit' : 'new',
        value: isExisting ? transaction : null,
        transactionType: transaction?.transactionType ?? 'expense',
        scheduleId,
        setupId,
        dueDate: occurDate,
      },
      { disableClose: true, autoFocus: false },
    );
  }

  ngOnDestroy() {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

}
